import {
  useEffect,
  useState,
  type AnchorHTMLAttributes,
  type ReactNode,
} from "react";
import {
  buildDashboardCreateUrl,
  DASHBOARD_URL,
} from "@/lib/dashboard-urls";

/**
 * Client-side href for dashboard signup, with attribution bridged after mount.
 */
export function useDashboardCreateHref(options?: { meno?: boolean }): string {
  const meno = options?.meno ?? false;
  const fallback = meno
    ? `${DASHBOARD_URL}/account/create?meno=true`
    : `${DASHBOARD_URL}/account/create`;

  const [href, setHref] = useState(fallback);

  useEffect(() => {
    setHref(buildDashboardCreateUrl({ meno }));
  }, [meno]);

  return href;
}

type DashboardCreateLinkProps = {
  children: ReactNode;
  className?: string;
  meno?: boolean;
  target?: AnchorHTMLAttributes<HTMLAnchorElement>["target"];
  rel?: string;
} & Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  "href" | "children" | "className" | "target" | "rel"
>;

/**
 * Client-side dashboard signup link that bakes stored marketing attribution
 * into the href after mount (SSR/hydration-safe fallback without params).
 */
export function DashboardCreateLink({
  children,
  className,
  meno = false,
  target,
  rel,
  ...rest
}: DashboardCreateLinkProps) {
  const href = useDashboardCreateHref({ meno });

  return (
    <a href={href} className={className} target={target} rel={rel} {...rest}>
      {children}
    </a>
  );
}
